<template>
<div :class="rootClasses">
  <!-- App shell (src/utils/followups/uiVersion.js key 'nav': Super Admins by default, office users per
       STAGE.nav and their one office choice): only the CHROME switches — the content column below is
       rendered once for both. -->
  <AppShell
    v-if="navUseNewShell"
    :alerts-prompt="alertsPrompt"
    @frame="onShellFrame"
    @nav-ui="setNavUi"
    @alerts-enable="enableBrowserNotifications"
    @alerts-not-now="dismissNotificationBanner"
    @alerts-sound-replay="replaySoundTest"
    @alerts-sound-dismiss="dismissSoundTestBanner"
  />
  <Sidebar v-else />
  <!-- <Helper /> -->
  <div class="wrap" ref="wrap">
    <Header v-if="!navUseNewShell" />
    <!-- <Chat /> -->
     <v-touch class="content" >
      <!-- The two alert-prompt cards below are for the classic header only. Under the app shell the same
           prompt is a row in the bell's popover plus a dot on the bell (alertsPrompt, passed to AppShell). -->
      <div v-if="showNotificationBanner && !navUseNewShell" class="np-card np-card--info">
        <div class="np-card__icon"><i class="fa fa-bell"></i></div>
        <div class="np-card__content">
          <h4 class="np-card__title">Get alerts for new chats &amp; SMS</h4>
          <p class="np-card__text">Click Enable to allow desktop notifications. Sound is a separate browser setting — make sure "Sound" is allowed in your site settings too.</p>
        </div>
        <div class="np-card__actions">
          <button type="button" class="np-card__cta" @click="enableBrowserNotifications">Enable</button>
          <button type="button" class="np-card__dismiss" @click="dismissNotificationBanner" title="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <div v-if="showSoundTestBanner && !navUseNewShell" class="np-card" :class="soundTestVariantClass">
        <div class="np-card__icon"><i :class="'fa ' + soundTestIconClass"></i></div>
        <div class="np-card__content">
          <h4 class="np-card__title">{{ soundTestTitle }}</h4>
          <p class="np-card__text" v-html="soundTestBody"></p>
        </div>
        <div class="np-card__actions">
          <button type="button" class="np-card__cta" @click="replaySoundTest">Test again</button>
          <button type="button" class="np-card__dismiss" @click="dismissSoundTestBanner" title="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <!-- Below zero with nothing to refill it: NOT dismissible (sends stop at the limit). -->
      <div v-if="showNegativeBalanceBanner" class="np-card np-card--danger np-card--low-balance" data-test="negative-balance-banner">
        <div class="np-card__icon"><i class="fa fa-exclamation-circle"></i></div>
        <div class="np-card__content">
          <h4 class="np-card__title">{{ walletNegativeBalanceCopy.title }}</h4>
          <p class="np-card__text">{{ walletNegativeBalanceCopy.text }}</p>
        </div>
        <div class="np-card__actions">
          <button type="button" class="np-card__cta" @click="goManageCredits">Manage Credits</button>
          <a
            class="np-card__learn"
            href="https://docs.teamio.app/billing/costs-and-delivery/"
            target="_blank"
            rel="noopener"
          >Learn more</a>
        </div>
      </div>
      <div v-if="showLowBalanceBanner" class="np-card np-card--danger np-card--low-balance">
        <div class="np-card__icon"><i class="fa fa-exclamation-triangle"></i></div>
        <div class="np-card__content">
          <h4 class="np-card__title">{{ lowBalanceCopy.title }}</h4>
          <p class="np-card__text">{{ lowBalanceCopy.text }}</p>
        </div>
        <div class="np-card__actions">
          <button type="button" class="np-card__cta" @click="goManageCredits">Manage Credits</button>
          <a
            class="np-card__learn"
            href="https://docs.teamio.app/billing/costs-and-delivery/"
            target="_blank"
            rel="noopener"
          >Learn more</a>
          <button type="button" class="np-card__dismiss" @click="dismissLowBalanceBanner" title="Dismiss">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>
      <DemoBanner />
      <NewUiWelcomeBanner />
      <FeatureNudge v-if="featureTipsEnabled" />
      <transition name="router-animation">
        <router-view :key="$route.fullPath"/>
      </transition>
      <footer class="contentFooter">
        &copy; Teamio.app
        </footer>
    </v-touch>
  </div>
</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import Push from 'push.js';
import { detectSiteAudioMuted } from '@/utils/audioMuteDetection';

import Sidebar from '@/components/Sidebar/Sidebar';
import Header from '@/components/Header/Header';
import AppShell from '@/components/AppShell/AppShell.vue';
import { shouldUseNewFollowupsUi, setNavUiChoice, browserStorage } from '@/utils/followups/uiVersion';
import FeatureNudge from '@/components/FeatureNudge.vue';
import NewUiWelcomeBanner from '@/components/Layout/NewUiWelcomeBanner.vue';
import DemoBanner from '@/components/Layout/DemoBanner.vue';
//import Chat from '@/components/Chat/Chat';
//import Helper from '@/components/Helper/Helper';
import mixin from '@/mixin';
import { throttledCheck, CHECK_INTERVAL, setStaleBundleHandler } from '@/services/versionCheck';
import { installSessionHealthGuard } from '@/plugins/sessionHealthGuard';
import { hasSmsNumber, canUseConversations } from '@/utils/siteCapability';
import { effectiveLowBalance } from '@/utils/negativeBalanceBanner';
import { soundTestCopy, alertsPromptFor } from '@/utils/notificationPromptCopy';

import './Layout.scss';

export default {
  name: 'Layout',
  //components: { Sidebar, Header, Chat, Helper },
  components: { Sidebar, Header, FeatureNudge, AppShell, NewUiWelcomeBanner, DemoBanner },
  mixins: [mixin],
  data() {
    return {
      versionCheckInterval: null,
      updateAvailable: false,
      // Set when a new version is detected so we show the refresh nudge once.
      versionToastId: null,
      showNotificationBanner: false,
      showSoundTestBanner: false,
      soundTestResult: 'unknown', // 'audible' | 'muted' | 'unknown'
      // Bumped on dismiss so the localStorage-backed `lowBalanceDismissed` computed
      // re-evaluates (localStorage reads aren't reactive on their own).
      lowBalanceDismissBump: 0,
      // App shell gate: bumped when the Super Admin flips classic <-> new (localStorage is not
      // reactive), and the frame AppShell reports so .wrap can make room for its fixed chrome.
      navUiVersion: 0,
      shellMode: 'desktop',
      shellCollapsed: false
    }
  },
  methods: {
    ...mapActions(
      'layout', ['switchSidebar', 'handleSwipe', 'changeSidebarActive'],
    ),

    // 'old' = the shell's "Classic navigation", 'new' = the classic header's "New navigation".
    setNavUi(target) {
      setNavUiChoice(browserStorage(), target);
      this.navUiVersion += 1;
    },

    // AppShell emits { mode: 'desktop'|'tablet'|'phone', collapsed } on mount and on every change.
    onShellFrame(frame) {
      const f = frame || {};
      this.shellMode = f.mode === 'tablet' || f.mode === 'phone' ? f.mode : 'desktop';
      this.shellCollapsed = f.collapsed === true;
    },

    maybeShowNotificationBanner() {
      try {
        if (typeof Notification === 'undefined') return;
        if (Notification.permission !== 'default') return;
        if (localStorage.getItem('notificationPromptDismissed') === 'true') return;
        this.showNotificationBanner = true;
      } catch (_) { /* ignore */ }
    },

    enableBrowserNotifications() {
      // User clicked Enable — whatever the browser-level prompt does next,
      // don't show this banner again. If the OS prompt is dismissed (user
      // X's it or clicks away), Notification.permission stays 'default'
      // and our maybeShowNotificationBanner gate would re-show the banner
      // on every page load. Treat the Enable click itself as explicit
      // consent-to-stop-asking — if they actually need the OS-level
      // permission granted later, they can enable it in browser settings.
      try { localStorage.setItem('notificationPromptDismissed', 'true'); } catch (_) { /* ignore */ }
      const finish = () => {
        this.showNotificationBanner = false;
        this.runSoundTest();
      };
      try {
        Push.Permission.request(finish, finish);
      } catch (_) {
        finish();
      }
    },

    async runSoundTest() {
      // Honor the user's prior "don't show me this again" — if they dismissed
      // the banner before, skip the test entirely. Mirrors the
      // notificationPromptDismissed gate above; without this the banner
      // would reappear every session even after dismissal.
      try {
        if (localStorage.getItem('soundTestDismissed') === 'true') return;
      } catch (_) { /* ignore */ }

      // Play a short test tone so the user immediately learns whether sound
      // is actually working. The Sound permission is browser-level with no
      // JS API to request it, so we use AudioContext state as a proxy to
      // detect the Chrome per-site mute and tailor the banner copy.
      this.playSound('/sounds/simple-notification-152054.mp3');
      this.showSoundTestBanner = true;
      try {
        this.soundTestResult = await detectSiteAudioMuted();
      } catch (_) {
        this.soundTestResult = 'unknown';
      }
    },

    dismissNotificationBanner() {
      try { localStorage.setItem('notificationPromptDismissed', 'true'); } catch (_) { /* ignore */ }
      this.showNotificationBanner = false;
    },

    replaySoundTest() {
      // User explicitly asked to replay — clear the dismissal so the test
      // can run again. Otherwise the early-return above would block it.
      try { localStorage.removeItem('soundTestDismissed'); } catch (_) { /* ignore */ }
      this.runSoundTest();
    },

    dismissSoundTestBanner() {
      try { localStorage.setItem('soundTestDismissed', 'true'); } catch (_) { /* ignore */ }
      this.showSoundTestBanner = false;
    },

    // Dismiss the low-balance banner for THIS site + THIS channel combination only.
    // Keying by siteId + which channels are low means dismissing an SMS-only alert
    // does NOT suppress a later email (or SMS+email) alert — that's a different key.
    // The dismissal is cleared again when the alert clears (see the watcher below), so
    // it re-appears if the balance recovers and later drops again — never a permanent mute.
    dismissLowBalanceBanner() {
      const key = this.lowBalanceDismissKey;
      if (key) { try { localStorage.setItem(key, '1'); } catch (_) { /* ignore */ } }
      this.lowBalanceDismissBump += 1;
    },

    // Route to the Credits tab via the SPA router (mirrors CreditBalancePill.goBuyCredits)
    // — never a raw href, so it stays a client-side navigation.
    goManageCredits() {
      const target = { path: '/app/my-account', query: { tab: 'credits' } };
      if (this.$route.path !== '/app/my-account') {
        this.$router.push(target).catch(() => {});
      } else if (this.$route.query.tab !== 'credits') {
        this.$router.replace(target).catch(() => {});
      }
    },

    // Clear any low-balance dismissals for the current site (all channel combos) so a
    // fresh low-balance episode after a recovery shows the banner again.
    clearLowBalanceDismissals() {
      const siteID = this.$store.state.customer && this.$store.state.customer.siteID;
      if (!siteID) return;
      const prefix = `lowBalanceDismissed:${siteID}:`;
      try {
        for (const combo of ['sms', 'email', 'both']) {
          localStorage.removeItem(prefix + combo);
        }
      } catch (_) { /* ignore */ }
      this.lowBalanceDismissBump += 1;
    },

    playSound (sound) {
      if(sound) {
        let audio = new Audio(sound);
        // .play() returns a Promise that rejects if autoplay is blocked.
        // Catch silently so a blocked play doesn't surface as an unhandled rejection.
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => { /* autoplay blocked or no user gesture yet */ });
        }
      }
    },

    // Attach Firestore listener for task-assignment notifications.
    // Fires sound + push when a new type='TASK' notification doc lands for this user.
    async initializeTaskNotifications() {
      try {
        await this.waitForUserAndSettings();
        const userID = this.$store.state.customer?.userID;
        if (!userID) return;
        await this.$store.dispatch('taskNotifications/attachListener', { employeeID: userID });
      } catch (e) {
        console.error('[Layout] Error initializing task notifications:', e);
      }
    },

    // Initialize conversations for sidebar indicator
    async initializeConversationsForSidebar() {
      try {
        // Wait for user authentication and settings to be available
        await this.waitForUserAndSettings();

        const siteID = this.$store.state.customer?.siteID;
        if (!siteID) {
          return;
        }

        // Conversations = a texting number OR WhatsApp (same rule as config/ability.js).
        if (!canUseConversations(this.allSettings)) {
          return;
        }

        // Initialize conversations store for unread detection
        await this.$store.dispatch('conversationsV2/initUnreadBaseline');
        await this.$store.dispatch('conversationsV2/loadLastReadMap');
        await this.$store.dispatch('conversationsV2/initRoomsListener', { siteID });

      } catch (error) {
        console.error('[Layout] Error initializing conversations for sidebar:', error);
      }
    },

    // Initialize the real-time SMS credit balance (header pill). Seeds over HTTP
    // then subscribes to siteWallet/{siteID}. Idempotent + gated on a live siteID,
    // mirroring initializeConversationsForSidebar.
    async initializeCreditBalance() {
      try {
        await this.waitForUserAndSettings();
        const siteID = this.$store.state.customer?.siteID;
        if (!siteID) return;
        // Demo site (plan Task 6.5): no wallet; credits are not part of the demo.
        if (this.$store && this.$store.getters['customer/isDemo'] === true) return;

        // Only SMS-enabled sites (a provisioned GENERAL_PHONE texting number) have a credit
        // wallet — skip the seed + Firestore subscription otherwise. Mirrors the gate on
        // initializeConversationsForSidebar, and avoids a needless Lambda call + real-time
        // read for every non-texting site.
        // hasSmsNumber: a WhatsApp-only office still has a wallet.
        if (!hasSmsNumber(this.allSettings)) return;

        await this.$store.dispatch('wallet/initBalance', siteID);
      } catch (error) {
        console.error('[Layout] Error initializing credit balance:', error);
      }
    },

    // Initialize force-logout listener for session revocation
    // This ensures the watchMetaData listener is always active, even after page refresh
    async initializeForceLogoutListener() {
      try {
        const user = this.$store.state.customer?.user;
        if (user && user.FirebaseUID) {
          // Set up the metadata watcher - this will trigger logout if revokeTime is already set
          // Do NOT clear metadata here as that would remove a pending revokeTime signal
          await this.$store.dispatch('customer/watchMetaData', user.FirebaseUID);
          console.log('[Layout] Force-logout listener initialized for user:', user.FirebaseUID);
        }
      } catch (error) {
        console.error('[Layout] Error initializing force-logout listener:', error);
      }
    },

    // Version check: when a new deployment is detected we get the user onto the
    // new bundle WITHOUT ever logging them out — a new frontend bundle only
    // needs a page reload, not a re-auth. (Force-logging-out to apply an update
    // produced a logout loop for clients stuck on a stale bundle — Smile Bar,
    // 2026-05: logout -> reload -> still stale -> logged out again every ~10-15
    // min.) Freshness is now fully decoupled from auth:
    //   - Active user: show a non-blocking "refresh" nudge they click when ready.
    //   - Backgrounded tab: silently reload now, so they return to the latest
    //     build already logged in. This replaces the old nightly forced logout
    //     as the daily-freshness backstop — most tabs get backgrounded at some
    //     point (switching apps, end of day), which applies the update for them.
    async runVersionCheck() {
      if (this.updateAvailable) return; // Already handling this version

      const hasUpdate = await throttledCheck();
      if (hasUpdate) this.offerOrApplyUpdate();
    },

    // The ONE implementation of "we are on an old bundle, get them onto the new
    // one". Two callers: the version poll above, and the stale-bundle safety net
    // (a chunk that 404'd — see installStaleBundleHandlers in Routes.js). Both
    // mean the same thing to the user, so neither gets its own toast.
    offerOrApplyUpdate() {
      this.updateAvailable = true;
      if (document.hidden) {
        // Tab is in the background — reload right away. Nobody is mid-task,
        // so there's nothing to interrupt and no nudge worth showing.
        this.applyVersionUpdate();
      } else {
        this.showUpdateToast();
      }
    },

    showUpdateToast() {
      // vue-toastification: pass an explicit id so repeat detections update the
      // existing toast in place instead of stacking duplicates. The toast is
      // persistent (timeout:false) but dismissible (closeButton) so users can
      // refresh on their own schedule — e.g. between patients.
      this.versionToastId = `version-update-${Date.now()}`;
      this.$toast.info('A new version of Teamio is available. Click here to refresh.', {
        id: this.versionToastId,
        timeout: false,
        closeOnClick: false, // body click = refresh; close button = dismiss
        draggable: false,
        closeButton: true,
        onClick: () => this.applyVersionUpdate(),
      });
    },

    // The session-health guard could not restore the sign-in quietly. Non-blocking:
    // the parts of Teamio that still work keep working; one click signs in again and
    // returns to this page (customer/logout passes redirectUrl through /login).
    showSessionExpiredPrompt() {
      this.$toast.warning('Your Teamio sign-in has expired. Click here to sign in again and come back to this page.', {
        id: 'session-expired',
        timeout: false,
        closeOnClick: false,
        draggable: false,
        closeButton: true,
        onClick: () => this.$store.dispatch('customer/logout', {
          reload: true,
          msg: 'Please sign in again to continue.',
          redirectUrl: this.$route && this.$route.fullPath,
        }),
      });
    },

    // Registered with versionCheck at mount; called when a chunk fetch 404s
    // (see installStaleBundleHandlers in Routes.js). Reuses offerOrApplyUpdate
    // so the user sees exactly the toast/quiet-reload they'd get from the poll.
    handleStaleBundle(reason) {
      console.log('[Layout] Stale bundle detected, offering the update:', reason);
      if (this.updateAvailable) return; // a nudge is already up / a reload already queued
      this.offerOrApplyUpdate();
    },

    applyVersionUpdate() {
      // Reload to pick up the new bundle WITHOUT touching the session. index.html
      // is served no-store, so a normal reload fetches the latest HTML + hashed
      // bundles; the user stays logged in (no Microsoft re-auth, no /login bounce).
      window.location.reload();
    },

    // Visibility API drives the no-logout freshness path:
    //   - leaving the tab: if an update is already pending, apply it now while
    //     they're away (silent reload) so the next focus shows the latest build.
    //   - returning to the tab: check for a freshly-deployed version.
    handleVisibilityChange() {
      if (document.hidden) {
        if (this.updateAvailable) this.applyVersionUpdate();
      } else {
        this.runVersionCheck();
      }
    },

    // Helper method to wait for user authentication and settings
    async waitForUserAndSettings() {
      return new Promise((resolve) => {
        const checkReady = () => {
          const user = this.$store.state.customer?.user;
          const siteID = this.$store.state.customer?.siteID;
          const settings = this.$store.state.customer?.settings;

          // CRITICAL: Wait for settings to be loaded before checking GENERAL_PHONE
          if (user && siteID && settings && settings.length > 0) {
            resolve();
          } else {
            // Check again in 100ms
            setTimeout(checkReady, 100);
          }
        };

        checkReady();
      });
    }
  },
  computed: {
    // The "Tip" / "Coming Soon" card (FeatureNudge). OFF unless VUE_APP_FEATURE_TIPS_ENABLED=true is set
    // at build time (2026-10-13: three stacked banners pushed page content off the screen). While off
    // the card is not mounted, so it does not even fetch the tips. The tips themselves and the admin
    // manager (Gautam/NudgeManager.vue) are untouched.
    featureTipsEnabled() {
      return process.env.VUE_APP_FEATURE_TIPS_ENABLED === 'true';
    },
    ...mapState('layout', {
      sidebarClose: state => state.sidebarClose,
      sidebarStatic: state => state.sidebarStatic,
      chatOpen: state => state.chatOpen,
    }),

    // Super Admins get the new app shell unless they chose "Classic navigation" (teamio_nav_ui);
    // everyone else follows STAGE.nav and their one office choice (the classic Sidebar + Header at
    // "preview"). An office switch reloads the page, so only the Super Admin switch bumps navUiVersion.
    navUseNewShell() {
      // eslint-disable-next-line no-unused-expressions
      this.navUiVersion;
      return shouldUseNewFollowupsUi('nav', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    rootClasses() {
      const shell = this.navUseNewShell;
      return {
        root: true,
        chatOpen: this.chatOpen,
        sidebarClose: this.sidebarClose,
        sidebarStatic: this.sidebarStatic,
        'root--shell': shell,
        'root--shell-desktop': shell && this.shellMode === 'desktop',
        'root--shell-tablet': shell && this.shellMode === 'tablet',
        'root--shell-phone': shell && this.shellMode === 'phone',
        'root--shell-collapsed': shell && this.shellCollapsed,
      };
    },
    // The words come from utils/notificationPromptCopy.js, shared with the app shell's bell popover.
    soundTestVariantClass() {
      return 'np-card--' + soundTestCopy(this.soundTestResult).variant;
    },
    soundTestIconClass() {
      return soundTestCopy(this.soundTestResult).icon;
    },
    soundTestTitle() {
      return soundTestCopy(this.soundTestResult).title;
    },
    soundTestBody() {
      return soundTestCopy(this.soundTestResult).body;
    },
    // What the app shell shows in the bell: { phase: 'ask'|'sound'|'', result }.
    alertsPrompt() {
      return alertsPromptFor({
        showAsk: this.showNotificationBanner,
        showSound: this.showSoundTestBanner,
        soundResult: this.soundTestResult,
      });
    },

    // ---- Low credit-balance banner ----------------------------------------------------
    // Server-authoritative flags ({ sms, email, any }) from the wallet store getter.
    ...mapGetters('wallet', {
      walletLowBalance: 'lowBalanceAlert',
      walletNegativeBalance: 'negativeBalance',
      walletNegativeBalanceCopy: 'negativeBalanceCopy',
    }),
    ...mapState('wallet', { walletHasBalance: 'hasBalance' }),

    // Wallet feature flag — SAME gate the header CreditBalancePill uses.
    walletFlagEnabled() {
      return process.env.VUE_APP_SMS_WALLET_ENABLED === 'true';
    },

    // Who should see the low-balance banner: not the narrow campaigns/conversations gate the
    // *pill* uses (a billing Admin who works neither surface would then MISS the "add credits"
    // alert — exactly the person who must act). Low credits stop ALL of the practice's texts
    // (appointment reminders included), so the right predicate is "SMS is enabled here" (below) +
    // "this user can reach the Manage Credits tab" — i.e. `view myaccount`, which the app grants to
    // the billing-relevant roles (Super Admin / Admin / Manager) and which is precisely the CTA's
    // own route permission, so the banner never dead-ends. (The siteWallet subscription is already
    // gated on SMS-enabled only, so the flag data is present for these users.) Try/catch covers the
    // pre-rules-loaded window.
    canManageCredits() {
      try {
        return this.$can('view', 'myaccount');
      } catch (_) {
        return false;
      }
    },

    // Site has a provisioned texting number → it has a wallet (not the preview/upsell
    // state). Same GENERAL_PHONE signal the pill gates on (via mixin.allSettings).
    smsFeatureEnabled() {
      return hasSmsNumber(this.allSettings);
    },

    // Low flags minus any channel that is below zero — the "run out" banner supersedes "running low"
    // for that channel (utils/negativeBalanceBanner.effectiveLowBalance).
    lowBalanceEffective() {
      return effectiveLowBalance(this.walletLowBalance, this.walletNegativeBalance);
    },

    // Same gate as the low-balance banner; never dismissible.
    showNegativeBalanceBanner() {
      return this.lowBalanceBannerGateOpen
        && this.walletNegativeBalance.any
        && !!this.walletNegativeBalanceCopy;
    },

    // Which channel(s) are low → 'sms' | 'email' | 'both' | '' (drives copy + dismiss key).
    lowBalanceChannelsKey() {
      const a = this.lowBalanceEffective;
      if (a.sms && a.email) return 'both';
      if (a.sms) return 'sms';
      if (a.email) return 'email';
      return '';
    },

    // Per-site + per-channel-combo dismiss key, so an SMS dismiss can't hide a later
    // email (or SMS+email) alert.
    lowBalanceDismissKey() {
      const siteID = this.$store.state.customer && this.$store.state.customer.siteID;
      const combo = this.lowBalanceChannelsKey;
      if (!siteID || !combo) return null;
      return `lowBalanceDismissed:${siteID}:${combo}`;
    },

    // Reactive read of the dismissal. `bump` (always >= 0) is referenced so this computed
    // establishes a reactive dependency and re-evaluates when we dismiss/clear — plain
    // localStorage reads are not reactive on their own.
    lowBalanceDismissed() {
      const bump = this.lowBalanceDismissBump;
      const key = this.lowBalanceDismissKey;
      if (!key) return false;
      try { return bump >= 0 && localStorage.getItem(key) === '1'; } catch (_) { return false; }
    },

    // Show when: SMS is enabled here (site has a wallet), the wallet feature is live (flag or a
    // loaded balance doc), and this user can reach the Manage Credits tab. Keyed on SMS-enabled +
    // billing-role, NOT the pill's campaigns/conversations permission — see canManageCredits.
    lowBalanceBannerGateOpen() {
      if (this.$store && this.$store.getters['customer/isDemo'] === true) return false;
      if (!this.smsFeatureEnabled) return false;
      if (!this.canManageCredits) return false;
      return this.walletFlagEnabled || this.walletHasBalance;
    },

    showLowBalanceBanner() {
      return this.lowBalanceBannerGateOpen
        && this.lowBalanceEffective.any
        && !this.lowBalanceDismissed;
    },

    // Copy adapts to which channel(s) are low. First sentence = title, rest = body.
    lowBalanceCopy() {
      const combo = this.lowBalanceChannelsKey;
      if (combo === 'both') {
        return {
          title: 'Your SMS and email credit balances are running low',
          text: 'Some patient texts and emails — reminders, recall, confirmations, forms, and replies — may stop sending. Turn on auto-reload or add credits to stay covered.',
        };
      }
      if (combo === 'email') {
        return {
          title: 'Your email credit balance is running low',
          text: 'Some patient emails — confirmations, recall, forms, and review requests — may stop sending. Turn on auto-reload or add credits to stay covered.',
        };
      }
      return {
        title: 'Your SMS credit balance is running low',
        text: 'Some patient texts — appointment reminders, recall messages, and replies — may stop sending. Turn on auto-reload or add credits to stay covered.',
      };
    },
  },
  watch: {
    // Trigger 1: Check version on route changes (throttled - at most once per 5 min)
    '$route'() {
      this.runVersionCheck();
    },
    // When the low-balance alert clears (balance recovered), drop any dismissals for the
    // current site so a fresh low episode later re-shows the banner (never a permanent mute).
    'walletLowBalance.any'(now, prev) {
      if (prev && !now) this.clearLowBalanceDismissals();
    }
  },
  created() {
    // Classic header gear "New navigation" (Header.vue useNewNavigation).
    this.$root.$on('teamio:nav-ui', this.setNavUi);
    /*
    const staticSidebar = JSON.parse(localStorage.getItem('sidebarStatic'));

    if (staticSidebar) {
      this.$store.state.layout.sidebarStatic = true;
    } else if (!this.sidebarClose) {
      setTimeout(() => {
        this.switchSidebar(true);
        this.changeSidebarActive(null);
      }, 2500);
    }
    */
    /*
    if (window.localStorage.getItem('authenticated') === 'true') {
      this.$router.push('/app/tasks/YourTasks');
    }
    */
  },

  mounted() {
    // Initialize force-logout listener (watchMetaData) on every page load
    // This ensures users can be force-logged out even after page refresh
    this.$nextTick(() => {
      this.initializeForceLogoutListener();
    });

    // Prompt for browser notification permission via a user-gesture banner
    // (modern browsers require a click to reliably show the permission dialog).
    this.maybeShowNotificationBanner();

    // Initialize conversations listener for sidebar indicator
    // This ensures unread message detection works immediately on login
    this.$nextTick(() => {
      setTimeout(() => {
        this.initializeConversationsForSidebar();
        this.initializeTaskNotifications();
        this.initializeCreditBalance();
      }, 2000); // Delay to ensure everything is loaded
    });

    // Trigger 2: Periodic version check (backup for idle users)
    this.versionCheckInterval = setInterval(() => {
      this.runVersionCheck();
    }, CHECK_INTERVAL);

    // Trigger 3: Check when user switches back to this tab (and apply a pending
    // update when they switch away — see handleVisibilityChange).
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Trigger 4: a chunk that 404'd. Same outcome as a detected new version, so
    // it borrows this component's path instead of growing its own.
    setStaleBundleHandler(this.handleStaleBundle);

    // Session health: a tab whose Firebase sign-in died (revoked while the tab was
    // closed, or lost from browser storage) heals itself, or asks the user to sign
    // in again. Layout only mounts on logged-in pages. See src/services/sessionHealth.js.
    this.teardownSessionHealth = installSessionHealthGuard({
      store: this.$store,
      router: this.$router,
      showSignInPrompt: this.showSessionExpiredPrompt,
    });
  },

  beforeDestroy() {
    this.$root.$off('teamio:nav-ui', this.setNavUi);

    // Clean up version check
    if (this.versionCheckInterval) {
      clearInterval(this.versionCheckInterval);
      this.versionCheckInterval = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    // Drop the stale-bundle handler with the component that owns it; without a
    // Layout mounted, versionCheck falls back to a plain reload.
    setStaleBundleHandler(null);
    if (this.teardownSessionHealth) {
      this.teardownSessionHealth();
      this.teardownSessionHealth = null;
    }

    // Clean up conversations listeners when layout is destroyed
    try {
      this.$store.dispatch('conversationsV2/detachAll');
    } catch (error) {
      console.error('[Layout] Error cleaning up conversations listeners:', error);
    }

    try {
      this.$store.dispatch('taskNotifications/detach');
    } catch (error) {
      console.error('[Layout] Error cleaning up task notifications listener:', error);
    }

    try {
      this.$store.dispatch('wallet/teardownBalance');
    } catch (error) {
      console.error('[Layout] Error cleaning up wallet listener:', error);
    }
  },
};
</script>

<style src="./Layout.scss" lang="scss" />
